import React, { useRef, useState } from "react";
import { UploadCloud, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";

// Reusable image upload field for the admin CMS. Stores a portable "/api/files/..." URL.
export default function ImageUpload({ value, onChange, label, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded.");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {label && <label className="eyebrow block mb-2">{label}</label>}
      <div className="flex items-start gap-4">
        <div
          className="relative w-40 h-28 shrink-0 border border-dashed border-primary-gold/30 bg-obsidian flex items-center justify-center overflow-hidden cursor-pointer hover:border-primary-gold/60 transition-colors"
          onClick={() => inputRef.current?.click()}
          data-testid={testid ? `${testid}-dropzone` : "image-dropzone"}
        >
          {value ? (
            <>
              <img src={mediaUrl(value)} alt="preview" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                className="absolute top-1 right-1 bg-obsidian/80 text-ivory p-1 hover:text-destructive"
                data-testid={testid ? `${testid}-clear` : "image-clear"}
              ><X size={14} /></button>
            </>
          ) : busy ? (
            <Loader2 size={22} className="text-primary-gold animate-spin" />
          ) : (
            <div className="text-center text-muted-text px-2">
              <UploadCloud size={20} className="mx-auto mb-1" />
              <span className="text-[10px] uppercase tracking-wide">Upload</span>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <input
            className="field text-xs"
            placeholder="…or paste an image URL"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid ? `${testid}-url` : "image-url"}
          />
          <p className="text-[11px] text-muted-text mt-2">Upload a file (jpg, png, webp, svg — max 10MB) or paste an external URL.</p>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={testid ? `${testid}-input` : "image-input"}
      />
    </div>
  );
}
